// Pre-Order Policy — support-side policy page (copy SSOT: copy-strings.json
// "preorder_policy"). Contact channels come from BUSINESS/company SSOT —
// never hardcode email/phone here.
import PageShell from "../components/PageShell";
import copy from "../lib/catalog/copy-strings.json";
import { BUSINESS } from "../constants/business";
import company from "../data/company.json";

function Section({ id, title, children }) {
        return (
                <section className="mt-14" data-testid={`preorder-policy-${id}`}>
                        <h2 className="font-mono text-[12px] tracking-[0.16em] uppercase text-[#F59E0B]">{title}</h2>
                        <div className="mt-4 space-y-3 text-white/80 leading-relaxed max-w-[68ch]">{children}</div>
                </section>
        );
}

export default function PreOrderPolicyPage() {
        const c = copy.preorder_policy;
        return (
                <PageShell title={c.title} path="/preorder-policy">
                        <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#F59E0B]">{c.eyebrow}</p>
                        <h1 className="mt-3 font-display uppercase leading-[0.95] text-[38px] md:text-[48px] lg:text-[64px]">
                                <span className="text-white">{c.heading_lead}</span>
                                <span className="text-[#F59E0B]">{c.heading_accent}</span>
                        </h1>
                        <p
                                data-testid="preorder-policy-effective"
                                className="mt-4 font-mono text-[11px] tracking-[0.18em] uppercase text-white/50"
                        >
                                {c.effective}
                        </p>
                        <p className="mt-6 text-white/80 leading-relaxed max-w-[68ch]">{c.intro}</p>

                        <Section id="what" title="What is a pre-order?">
                                {c.what.map((p, i) => <p key={i}>{p}</p>)}
                        </Section>

                        <Section id="dispatch" title="When your order is dispatched">
                                {c.dispatch.map((p, i) => <p key={i}>{p}</p>)}
                        </Section>

                        <Section id="delays" title="Why a pre-order can take longer than estimated">
                                <p>{c.delays_intro}</p>
                                <ul className="space-y-3">
                                        {c.delays.map((d) => (
                                                <li key={d.lead} className="border-l-2 border-white/15 pl-4">
                                                        <span className="text-white font-semibold">{d.lead}</span>{" "}
                                                        <span>{d.body}</span>
                                                </li>
                                        ))}
                                </ul>
                                <p>{c.delays_outro}</p>
                        </Section>

                        <section className="mt-14" data-testid="preorder-policy-cancel-customer">
                                <h2 className="font-mono text-[12px] tracking-[0.16em] uppercase text-[#F59E0B]">Cancellation by customers</h2>
                                <div className="mt-4 border border-[#F59E0B] bg-[#F59E0B]/10 p-5 max-w-[68ch]">
                                        <p className="text-white/85 leading-relaxed">{c.cancel_customer[0]}</p>
                                </div>
                                <div className="mt-4 space-y-3 text-white/80 leading-relaxed max-w-[68ch]">
                                        {c.cancel_customer.slice(1).map((p, i) => <p key={i}>{p}</p>)}
                                </div>
                        </section>

                        <section className="mt-14" data-testid="preorder-policy-cancel-us">
                                <h2 className="font-mono text-[12px] tracking-[0.16em] uppercase text-[#F59E0B]">Cancellation by us</h2>
                                <div className="mt-4 space-y-3 text-white/80 leading-relaxed max-w-[68ch]">
                                        <p>{c.cancel_us_intro}</p>
                                        <ul className="space-y-2">
                                                {c.cancel_us_reasons.map((r) => (
                                                        <li key={r} className="border-l-2 border-white/15 pl-4">{r}</li>
                                                ))}
                                        </ul>
                                        <p>{c.cancel_us_outro}</p>
                                </div>
                                <table
                                        data-testid="preorder-policy-refund-table"
                                        className="mt-5 w-full max-w-[520px] border-collapse font-sans text-[14px]"
                                >
                                        <thead>
                                                <tr className="border border-white/15 bg-white/[0.04] text-left">
                                                        <th className="px-4 py-3 font-mono text-[10px] tracking-[0.14em] uppercase text-white/60">
                                                                Payment method at checkout
                                                        </th>
                                                        <th className="px-4 py-3 font-mono text-[10px] tracking-[0.14em] uppercase text-white/60">
                                                                Refund processing time
                                                        </th>
                                                </tr>
                                        </thead>
                                        <tbody>
                                                {c.refund_table.map(([method, time]) => (
                                                        <tr key={method} className="border border-white/15">
                                                                <td className="px-4 py-3 text-white/85">{method}</td>
                                                                <td className="px-4 py-3 text-white/85">{time}</td>
                                                        </tr>
                                                ))}
                                        </tbody>
                                </table>
                        </section>

                        <Section id="mixed" title="Mixed carts (pre-order + in-stock together)">
                                {c.mixed.map((p, i) => <p key={i}>{p}</p>)}
                        </Section>

                        <Section id="warranty" title="Warranty and support">
                                {c.warranty.map((p, i) => <p key={i}>{p}</p>)}
                        </Section>

                        <Section id="address" title="Address changes">
                                {c.address.map((p, i) => <p key={i}>{p}</p>)}
                        </Section>

                        <section className="mt-14" data-testid="preorder-policy-contact">
                                <h2 className="font-mono text-[12px] tracking-[0.16em] uppercase text-[#F59E0B]">Contact and updates</h2>
                                <div className="mt-4 space-y-3 text-white/80 leading-relaxed max-w-[68ch]">
                                        <p>{c.contact_intro}</p>
                                        <p className="flex flex-col sm:flex-row gap-3 sm:gap-6">
                                                <a
                                                        href={`mailto:${BUSINESS.email}`}
                                                        data-testid="preorder-policy-email"
                                                        className="text-[#F59E0B] hover:underline"
                                                >
                                                        Email: {BUSINESS.email}
                                                </a>
                                                <a
                                                        href={BUSINESS.whatsappUrl}
                                                        target="_blank"
                                                        rel="noreferrer noopener"
                                                        data-testid="preorder-policy-whatsapp"
                                                        className="text-[#F59E0B] hover:underline"
                                                >
                                                        WhatsApp: {company.business_phone}
                                                </a>
                                        </p>
                                        <p>{c.contact_outro}</p>
                                </div>
                                <p
                                        data-testid="preorder-policy-agreement"
                                        className="mt-8 border-t border-white/10 pt-6 font-mono text-[11px] tracking-[0.16em] uppercase text-[#F59E0B]"
                                >
                                        {c.agreement}
                                </p>
                        </section>
                </PageShell>
        );
}
